<template>
  <div class="w-full">
    <button
      class="group flex w-full justify-between items-center rounded-[3px] text-[15px] font-normal text-white bg-[#4F83E3] hover:bg-[#6795EB] p-0 h-[38px] min-w-[218px]"
      :aria-label="`Buy ${template?.name || 'Template'}`"
      :disabled="loading"
      @click="openModal"
    >
      <div class="pl-[12px]">
        <span class="inline-flex">
          Buy Template —
          <Price
            :amount="template.product_rel?.cost"
            :currency-code="template.product_rel?.currency || 'USD'"
            :hide-currency-code="false"
            class="font-[500] ml-1"
          />
        </span>
      </div>

      <div class="relative flex items-center justify-center overflow-hidden h-[38px] w-[42px]">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.5673 0.666626C1.19911 0.666626 0.900635 0.965103 0.900635 1.33329C0.900635 1.70148 1.19911 1.99996 1.5673 1.99996H3.14639L3.87871 3.53769L4.77484 8.67052C3.72887 8.73543 2.90063 9.60431 2.90063 10.6666C2.90063 11.3748 3.2687 11.997 3.82396 12.3524C3.66054 12.6422 3.5673 12.9769 3.5673 13.3333C3.5673 14.4379 4.46273 15.3333 5.5673 15.3333C6.67187 15.3333 7.5673 14.4379 7.5673 13.3333C7.5673 13.0995 7.5272 12.8751 7.4535 12.6666H11.0144C10.9407 12.8751 10.9006 13.0995 10.9006 13.3333C10.9006 14.4379 11.7961 15.3333 12.9006 15.3333C14.0052 15.3333 14.9006 14.4379 14.9006 13.3333C14.9006 12.2287 14.0052 11.3333 12.9006 11.3333H4.90063C4.53244 11.3333 4.23397 11.0348 4.23397 10.6666C4.23397 10.2984 4.53244 9.99996 4.90063 9.99996H5.5579C5.58721 10.0003 5.61687 9.99879 5.64673 9.99521L13.9801 8.99521C14.2662 8.96088 14.4978 8.7466 14.5544 8.46404L15.5544 3.46404C15.5935 3.26819 15.5428 3.06509 15.4162 2.91063C15.2896 2.75617 15.1004 2.66663 14.9006 2.66663H4.94069L4.1692 1.04665C4.05864 0.814499 3.82443 0.666626 3.5673 0.666626H1.5673ZM12.9006 12.6666C12.5324 12.6666 12.234 12.9651 12.234 13.3333C12.234 13.7015 12.5324 14 12.9006 14C13.2688 14 13.5673 13.7015 13.5673 13.3333C13.5673 12.9651 13.2688 12.6666 12.9006 12.6666ZM4.90063 13.3333C4.90063 12.9651 5.19911 12.6666 5.5673 12.6666C5.93549 12.6666 6.23397 12.9651 6.23397 13.3333C6.23397 13.7015 5.93549 14 5.5673 14C5.19911 14 4.90063 13.7015 4.90063 13.3333ZM6.11534 8.59608L5.31292 3.99996H14.0874L13.3416 7.72892L6.11534 8.59608Z"
            fill="white"
          />
        </svg>
      </div>
    </button>

    <b-modal
      v-model="modal"
      hide-header
      hide-footer
      centered
      lazy
      dialog-class="!max-w-[600px] rounded-[.375rem]"
      body-class="!px-[3rem] !py-[3rem]"
      @hidden="resetForm"
    >
      <i
        class="fa fa-close z-[50] absolute top-[15px] right-[17px] text-[16px] text-[#6E747A] transition cursor-pointer hover:opacity-60"
        @click="closeModal"
      />

      <div
        v-if="product.cover"
        class="aspect-video rounded-[4px] bg-[#EDEDED] dark:bg-[#222b35] mb-4"
      >
        <img
          class="aspect-video rounded-[4px] object-cover"
          :src="product.cover"
          alt="Cover"
        >
      </div>

      <div>
        <div class="text-[25px] font-bold text-center mb-2">
          {{ product?.name }}
        </div>

        <p class="text-[#8A8A8A] text-[14px] text-center mb-6">
          {{ product?.description }}
        </p>

        <div class="text-[13px] font-[500] text-[#363636] mb-2">
          Payment Summary
        </div>
        <div class="flex items-center justify-between">
          <div class="text-[15px] font-bold text-[#363636]">
            {{ product.name }}
          </div>
          <div
            class="text-[15px] text-[#363636] capitalize flex items-center"
          >
            <Price
              :amount="product.cost"
              :currency-code="product.currency || 'USD'"
              :hide-currency-code="false"
              class="mr-1"
            />
            <span v-if="product.interval">
              / {{ product.interval }}
            </span>
          </div>
        </div>

        <template v-if="coupon && coupon.title">
          <hr class="my-4">

          <div class="flex items-center justify-between my-2">
            <div class="text-[15px] font-bold text-[#363636]">
              {{ coupon.title }}
            </div>

            <div class="text-[15px] text-[#363636] capitalize flex items-center">
              <Price
                :amount="discount"
                :currency-code="product.currency || 'USD'"
                :hide-currency-code="false"
                class="mr-1"
                :negative="true"
              />
            </div>
          </div>

          <div class="flex items-center justify-between my-2">
            <div class="text-[15px] font-bold text-[#363636]">
              Total due
            </div>

            <div class="text-[15px] text-[#363636] capitalize flex items-center">
              <span v-if="total === 0">
                Free
              </span>
              <Price
                v-else
                :amount="total"
                :currency-code="product.currency || 'USD'"
                :hide-currency-code="false"
                class="mr-1"
              />
            </div>
          </div>
        </template>

        <hr class="my-4">
      </div>

      <b-form
        novalidate
        @submit.prevent="onSubmit"
      >
        <b-form-group
          label="Payment Method"
          label-class="text-[14px] leading-[20px] font-[500] text-[#363636]"
          class="w-full mt-[1.25rem]"
        >
          <div class="inline-flex w-full items-center gap-[15px]">
            <v-select
              v-model="paymentMethod"
              :options="paymentMethods"
              class="flex h-[2.5rem] px-[0.625rem] py-[0.875rem] border border-[#D0D5DD] items-center bg-white sh shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] rounded"
              :searchable="false"
              label="name"
              required
              @input="handlePMSelectInput"
            />
            <div class="inline-flex flex-shrink-0 items-center gap-[4px]">
              <img
                v-if="!paymentMethod || !paymentMethod.brand || paymentMethod.brand === 'visa'"
                src="@/assets/icons/payment-systems/visa.svg"
              >
              <img
                v-if="!paymentMethod || !paymentMethod.brand || paymentMethod.brand === 'diners-club'"
                src="@/assets/icons/payment-systems/diners-club.svg"
              >
              <img
                v-if="!paymentMethod || !paymentMethod.brand || paymentMethod.brand === 'amex'"
                src="@/assets/icons/payment-systems/amex.svg"
              >
              <img
                v-if="!paymentMethod || !paymentMethod.brand || paymentMethod.brand === 'discover'"
                src="@/assets/icons/payment-systems/discover.svg"
              >
              <img
                v-if="!paymentMethod || !paymentMethod.brand || paymentMethod.brand === 'mastercard'"
                src="@/assets/icons/payment-systems/master-card.svg"
              >
            </div>
          </div>
        </b-form-group>

        <template v-if="paymentMethod && paymentMethod.id === -1">
          <StripeElements
            v-slot="{ elements }"
            class="w-full"
          >
            <b-form-group
              label="Card Number"
              label-class="text-[14px] leading-[20px] font-[500] text-[#363636]"
              class="mb-2.5"
            >
              <div class="flex h-[2.5rem] px-[0.625rem] py-[0.875rem] border border-[#D0D5DD] items-center bg-white sh shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] rounded">
                <StripeElement
                  type="cardNumber"
                  :elements="elements"
                  class="w-full h-[150%]"
                  :options="{
                    style: {
                      base: {
                        fontSize: '1rem',
                        color: '#000000',
                        fontFamily: 'ProximaNova, sans-serif'
                      }
                    },
                    showIcon: true,
                    disableLink: true
                  }"
                  @stripe-element="card = $event"
                />
              </div>
            </b-form-group>

            <div class="flex items-center gap-[.75rem]">
              <b-form-group
                label="Expiry Date"
                label-class="text-[14px] leading-[20px] font-[500] text-[#363636]"
                class="mb-2.5"
              >
                <div class="flex w-[5rem] h-[2.5rem] px-[0.625rem] py-[0.875rem] border border-[#D0D5DD] items-center bg-white sh shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] rounded">
                  <StripeElement
                    type="cardExpiry"
                    :elements="elements"
                    class="w-full h-[150%]"
                    :options="{
                      style: {
                        base: {
                          fontSize: '1rem',
                          color: '#000000',
                          fontFamily: 'ProximaNova, sans-serif'
                        }
                      }
                    }"
                  />
                </div>
              </b-form-group>

              <b-form-group
                label="CVC"
                label-class="text-[14px] leading-[20px] font-[500] text-[#363636]"
                class="mb-2.5"
              >
                <div class="flex items-center gap-[.75rem]">
                  <div class="flex w-[4rem] h-[2.5rem] px-[0.625rem] py-[0.875rem] border border-[#D0D5DD] items-center bg-white sh shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] rounded">
                    <StripeElement
                      type="cardCvc"
                      :elements="elements"
                      class="w-full h-[150%]"
                      :options="{
                        style: {
                          base: {
                            fontSize: '1rem',
                            color: '#000000',
                            fontFamily: 'ProximaNova, sans-serif'
                          }
                        }
                      }"
                    />
                  </div>

                  <svg
                    width="31"
                    height="27"
                    viewBox="0 0 31 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M29.493 0.517578H1.50294C0.674223 0.517578 0 1.19003 0 2.01658V19.9983C0 20.8248 0.674223 21.4972 1.50294 21.4972H4.59037C4.84113 21.4972 5.04442 21.2945 5.04442 21.0444C5.04442 20.7942 4.84113 20.5915 4.59037 20.5915H1.50294C1.17494 20.5915 0.908086 20.3254 0.908086 19.9983V8.49485H30.0879V19.9983C30.0879 20.3254 29.8211 20.5915 29.4931 20.5915H29.1624C29.4065 19.8179 29.5384 18.9951 29.5384 18.1421C29.5384 16.9805 29.2987 15.8576 28.8261 14.8047C28.7237 14.5765 28.4549 14.4743 28.2261 14.5764C27.9972 14.6787 27.8948 14.9466 27.9972 15.1748C28.4173 16.1106 28.6303 17.1089 28.6303 18.1421C28.6303 19.7815 28.0832 21.2956 27.1619 22.513H15.5211C14.5998 21.2956 14.0527 19.7815 14.0527 18.1421C14.0527 14.1336 17.3225 10.8725 21.3415 10.8725C23.6134 10.8725 25.7153 11.9018 27.1083 13.6964C27.2619 13.8941 27.547 13.9302 27.7452 13.7773C27.9435 13.6242 27.9798 13.3398 27.8263 13.142C26.2601 11.1241 23.8965 9.96681 21.3415 9.96681C16.8217 9.96681 13.1446 13.6343 13.1446 18.1422C13.1446 18.9952 13.2765 19.818 13.5206 20.5916H6.2147C5.96394 20.5916 5.76065 20.7943 5.76065 21.0445C5.76065 21.2947 5.96394 21.4974 6.2147 21.4974H13.8671C15.1565 24.3436 18.0286 26.3176 21.3415 26.3176C24.6529 26.3176 27.5259 24.3448 28.8159 21.4974H29.493C30.3217 21.4974 30.9959 20.8249 30.9959 19.9984V2.01658C30.996 1.19003 30.3218 0.517578 29.493 0.517578ZM16.333 23.4187H26.35C25.0431 24.6533 23.2795 25.4118 21.3415 25.4118C19.4035 25.4118 17.64 24.6533 16.333 23.4187ZM0.908086 7.58915V5.73239H23.6789C23.9298 5.73239 24.133 5.52969 24.133 5.27954C24.133 5.02938 23.9298 4.82669 23.6789 4.82669H0.908086V2.01658C0.908086 1.68944 1.17494 1.42328 1.50294 1.42328H29.4931C29.8211 1.42328 30.0879 1.68944 30.0879 2.01658V4.82669H25.3639C25.113 4.82669 24.9098 5.02938 24.9098 5.27954C24.9098 5.52969 25.113 5.73239 25.3639 5.73239H30.0879V7.58915H0.908086Z"
                      fill="#747E8C"
                    />
                    <path
                      d="M10.999 17.9939C11.9752 17.5275 12.5127 16.868 12.5127 16.137C12.5127 15.406 11.9752 14.7465 10.999 14.2801C10.1292 13.8646 8.98277 13.6357 7.77102 13.6357C6.55927 13.6357 5.41284 13.8646 4.54301 14.2801C3.56682 14.7465 3.0293 15.406 3.0293 16.137C3.0293 16.868 3.56688 17.5275 4.54301 17.9939C5.41284 18.4094 6.55921 18.6383 7.77102 18.6383C8.98283 18.6383 10.1292 18.4094 10.999 17.9939ZM3.93738 16.137C3.93738 15.779 4.3011 15.3999 4.93531 15.0969C5.68514 14.7387 6.69221 14.5414 7.77102 14.5414C8.84982 14.5414 9.85689 14.7388 10.6067 15.0969C11.2409 15.4 11.6047 15.779 11.6047 16.137C11.6047 16.495 11.2409 16.874 10.6067 17.1771C9.85689 17.5353 8.84982 17.7326 7.77102 17.7326C6.69221 17.7326 5.68514 17.5352 4.93531 17.1771C4.3011 16.8741 3.93738 16.495 3.93738 16.137Z"
                      fill="#747E8C"
                    />
                    <path
                      d="M18.2698 16.6606C18.4461 16.6606 18.616 16.7123 18.7611 16.8101C18.9688 16.9501 19.2512 16.8956 19.3915 16.6884C19.5319 16.4812 19.4773 16.1996 19.2695 16.0597C18.9737 15.8602 18.628 15.7549 18.2698 15.7549C17.2842 15.7549 16.4824 16.5546 16.4824 17.5376C16.4824 18.5205 17.2842 19.3202 18.2698 19.3202C18.667 19.3202 19.0246 19.1893 19.3034 18.9416C19.358 18.8932 19.4099 18.8398 19.4577 18.7829C19.6188 18.5913 19.5937 18.3057 19.4016 18.145C19.2095 17.9842 18.9231 18.0093 18.762 18.201C18.7424 18.2242 18.7215 18.2458 18.6997 18.2651C18.588 18.3643 18.4434 18.4145 18.2698 18.4145C17.7849 18.4145 17.3905 18.0211 17.3905 17.5376C17.3905 17.054 17.785 16.6606 18.2698 16.6606Z"
                      fill="#747E8C"
                    />
                    <path
                      d="M21.9623 16.0701L21.4573 17.5862L20.926 16.0595C20.8439 15.8232 20.585 15.6978 20.3482 15.7802C20.1114 15.8621 19.986 16.1202 20.0682 16.3565L20.9809 18.9793C21.0538 19.1838 21.2482 19.3205 21.4655 19.3205C21.4666 19.3205 21.4678 19.3205 21.469 19.3204C21.6877 19.319 21.8819 19.1792 21.9534 18.9696L22.8241 16.3556C22.9032 16.1182 22.7743 15.8619 22.5364 15.7831C22.2986 15.7042 22.0413 15.8327 21.9623 16.0701Z"
                      fill="#747E8C"
                    />
                    <path
                      d="M25.3159 16.0701L24.8109 17.5862L24.2795 16.0595C24.1974 15.8232 23.9385 15.6978 23.7018 15.7802C23.4649 15.8622 23.3395 16.1202 23.4217 16.3565L24.3345 18.9793C24.4073 19.1838 24.6017 19.3205 24.819 19.3205C24.8202 19.3205 24.8214 19.3205 24.8225 19.3204C25.0412 19.319 25.2355 19.1792 25.307 18.9696L26.1777 16.3556C26.2568 16.1182 26.128 15.8619 25.8899 15.7831C25.6522 15.7042 25.395 15.8327 25.3159 16.0701Z"
                      fill="#747E8C"
                    />
                  </svg>
                </div>
              </b-form-group>
            </div>
          </StripeElements>
        </template>

        <b-form-group v-if="template.enable_coupons">
          <b-input
            v-model="form.code"
            placeholder="Coupon code"
            class="flex !h-[2.5rem] px-[0.625rem] py-[0.875rem] border border-[#D0D5DD] items-center bg-white sh shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] rounded"
            @input="handleCoupon"
          />
        </b-form-group>

        <b-button
          type="submit"
          variant="primary"
          class="w-full h-[3.5rem] !font-[600] mt-4"
          :disabled="loading"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-2"
          />
          <span v-if="loading">
            Processing...
          </span>
          <span
            v-else
            class="inline-flex"
          >
            Buy
            <Price
              :amount="template.product_rel?.cost"
              :currency-code="template.product_rel?.currency || 'USD'"
              :hide-currency-code="false"
              class="font-[500]"
            />
          </span>
        </b-button>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash'
import Price from '@/components/utils/Price.vue'
import StripeElement from '@/components/stripe/Element.vue'
import StripeElements from '@/components/stripe/Elements.vue'
import {mapActions, mapState} from 'vuex'

export default {
  name: 'PurchaseButton',
  components: {
    Price,
    StripeElements,
    StripeElement
  },
  props: {
    template: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      modal: false,
      loading: false,
      serverError: null,
      success: false,
      card: null,
      methods: [],
      estageID: '38804',
      paymentMethod: {
        id: -1,
        name: 'Add New Card'
      },
      product: {},
      form: {
        number: '',
        exp_month: null,
        exp_year: null,
        cvc: '',
        name: '',
        email: '',
        country: '',
        zip_code: '',
        main: false,
        code: ''
      },
      total: null,
      discount: null,
      coupon: null,
      invalidCoupon: false,
      fullPriceCoupon: false,
      termsAccepted: false,
      timeout: null
    }
  },
  computed: {
    ...mapState('auth', {
      user: state => state.user
    }),
    paymentMethods () {
      const data  = _.cloneDeep(this.methods)
      data.push({
        id: -1,
        name: 'Add New Card'
      })
      return data
    },
    validEmail () {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)
    }
  },
  methods: {
    ...mapActions({
      buyProduct: 'subscription/buyProduct',
      checkCoupon: 'subscription/checkCoupon'
    }),
    openModal () {
      this.modal = true
    },
    closeModal () {
      this.modal = false
    },
    resetForm (data = {}) {
      this.loading = false
      this.success = false
      this.form = {
        number: data.number || '',
        exp_month: data.exp_month || null,
        exp_year: data.exp_year || null,
        cvc: data.cvc || '',
        name: data.name || '',
        email: data.email || '',
        country: data.country || '',
        zip_code: data.zip_code || '',
        main: false,
        coupon: (data.coupon != null ? data.coupon : (this.form && this.form.coupon) || '')
      }
      this.termsAccepted = false
    },
    resetCoupon () {
      this.invalidCoupon = false
      this.coupon = null
      this.total = null
      this.discount = null
      this.form.code = ''
      this.coupon = ''
      this.fullPriceCoupon = false
    },
    discountedPrice () {
      let originalPrice = this.product.price || this.product.cost
      if (typeof originalPrice === 'string') originalPrice = parseFloat(originalPrice)
      const type = this.coupon.discount_type
      let discounted = originalPrice
      if (type === 'percent') {
        const discountAmount = (originalPrice * this.coupon.discount) / 100
        this.discount = discountAmount
        discounted = originalPrice - discountAmount
      } else if (type === 'fixed') {
        this.discount = this.coupon.discount
        discounted = originalPrice - this.discount
      }
      this.total = Math.max(0, discounted)

      if (this.total === 0) {
        this.fullPriceCoupon = true
      } else {
        this.fullPriceCoupon = false
      }
    },
    async validateCoupon () {
      const val = this.form.code
      if (val.length) {
        this.coupon = await this.checkCoupon({
          product: this.product.id,
          coupon: val
        })
        if (this.coupon) {
          this.invalidCoupon = false
          this.discountedPrice()
        } else {
          this.invalidCoupon = true
          this.discount = null
          this.total = null
          this.fullPriceCoupon = false
        }
      }
    },
    endCouponInput () {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.validateCoupon()
      }, 1000)
    },
    handleCoupon (val) {
      if (!val.length) {
        this.resetCoupon()
      }
      this.form.code = val.toUpperCase().trim()
      this.coupon = val.toUpperCase().trim()
      this.endCouponInput()
    },
    async fetchProduct () {
      const {data} = await axios.get(`api/user/payment-systems/products/${this.template.product}`)
      this.product = data.data
    },
    async fetchPaymentMethods () {
      const {data} = await axios.get('api/user/settings/payment-methods')
      const cards = data.data.cards
      this.methods = (cards || []).map(c => {
        c.card.name = `Card ending in .. ${_.get(c,'card.last4')}`
        c.card.brand = _.get(c,'card.brand')
        return { ...c, ...c.card }
      })
    },
    handlePMSelectInput () {
      let data = {}
      this.$validator.reset()
      if (this.paymentMethod.id !== -1) {
        data = {
          number: `**** **** **** ${_.get(this.paymentMethod,'card.last4')}`,
          exp_month: _.get(this.paymentMethod,'card.exp_month'),
          exp_year: _.get(this.paymentMethod,'card.exp_year')
        }
      }
      this.resetForm(data)
    },
    async onSubmit () {
      const firstName = this.user.name.split(' ')[0]
      const lastName = this.user.name.split(' ')[1]

      let payload = {
        integration: 'stripe',
        product_id: String(this.product.id),
        project_id: this.estageID,
        coupon: this.form.coupon || undefined,
        first_name: firstName,
        last_name: lastName,
        email: this.user.email
      }

      const subscription = Boolean(this.product.interval)

      if (this.paymentMethod.id === -1) {
        this.loading = true

        await this.buyProduct({
          body: payload,
          subscription,
          payment_method: {
            card: this.card,
            billing_details: {
              email: this.user.email
            }
          }
        })
          .then(() => {
            this.$emit('success')
            this.closeModal()
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        await this.buyProduct({
          body: payload,
          subscription,
          pm: this.paymentMethod.id
        })
          .then(() => {
            this.$emit('success')
            this.closeModal()
          })
          .finally(() => {
            this.loading = false
          })
      }
    }
  },
  async mounted () {
    this.loading = true
    try {
      await this.fetchProduct()
      await this.fetchPaymentMethods()
      if (!this.paymentMethod || this.paymentMethod.id === -1) {
        const main = this.methods.find(m => m.main)
        this.paymentMethod = main || this.paymentMethods[0]
      }
      this.handlePMSelectInput()
    } catch (err) {
      const res = err && err.response
      this.serverError = (res && (res.data?.message || res.data?.error)) || err.message || 'Failed to load.'
      console.error('[PurchaseButton] init failed', res?.status, res?.data || err)
    } finally {
      this.loading = false
    }
  }
}
</script>